import { useState, type ReactNode } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { ChevronLeft, ExternalLink, Construction } from 'lucide-react'
import EvaluationPickerSheet from './EvaluationPickerSheet'

interface EvaluationModulePageProps {
	moduleName: string
	moduleDescription?: string
	// Linear ticket that ships the real workspace (E2 / B3 / D4 / etc.).
	// Ignored when `renderWorkspace` is provided (the workspace is live).
	workspacePhase: string
	// One-liner describing what the workspace will do once the phase ships
	workspaceHint: string
	// Render the real workspace once it lands. Callers pass this when their
	// phase ships (e.g. C5's ReportsPage, E6's ScoringPage). When provided,
	// replaces the "Construction" placeholder card without changing the
	// surrounding chrome (back-to-picker bar, "Open evaluation" link). When
	// omitted, the placeholder card renders.
	// phase ships (C5 was the first — ReportsPage wires `<ReportWorkspace />`).
	// When provided, replaces the "Construction" placeholder card without
	// changing the surrounding chrome (back-to-picker bar, "Open evaluation"
	// link). When omitted, the placeholder card renders.
	renderWorkspace?: (evalId: string) => ReactNode
}

// Shared shell for global module pages (Scoring, Financials, Risks, Pros&Cons,
// Approvals, Reports, Compare). Handles the AdminPanel + Header chrome, the
// `?eval=<id>` URL round-trip, and the picker → workspace switching. Each
// module's own page file is just a thin wrapper around this with its config.
//
// Until the workspace ships in its phase, the picked evaluation lands on a
// placeholder card pointing at the Linear issue. Once the workspace ships,
// the placeholder gets replaced with `<ModuleWorkspace evalId={evalId} />`.
const EvaluationModulePage = ({
	moduleName,
	moduleDescription,
	workspacePhase,
	workspaceHint,
	renderWorkspace,
}: EvaluationModulePageProps) => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [searchParams, setSearchParams] = useSearchParams()
	const evalId = searchParams.get('eval') || ''

	const evaluation = useSelector((state: any) =>
		evalId ? state.evaluations?.entities?.[evalId] : null,
	)

	const handlePick = (id: string) => {
		setSearchParams({ eval: id })
	}

	const handleClear = () => {
		setSearchParams({})
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<main className="flex-1 overflow-y-auto p-6">
					{!evalId ? (
						<EvaluationPickerSheet
							moduleName={moduleName}
							moduleDescription={moduleDescription}
							onPick={handlePick}
						/>
					) : (
						<div className="max-w-4xl mx-auto space-y-4">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3 min-w-0">
									<button
										type="button"
										onClick={handleClear}
										className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-200 text-gray-600 hover:border-[#0080fe] hover:text-[#0080fe] transition-colors"
									>
										<ChevronLeft className="w-4 h-4" />
										Change evaluation
									</button>
									<div className="min-w-0">
										<div className="text-xs text-gray-500">{moduleName}</div>
										<div className="font-semibold text-sm text-gray-900 truncate">
											{evaluation?.name ?? `Evaluation ${evalId}`}
										</div>
									</div>
								</div>
								<Link
									to={`/evaluations/${evalId}`}
									className="text-xs text-[#0080fe] hover:underline inline-flex items-center gap-1"
								>
									Open evaluation
									<ExternalLink className="w-3 h-3" />
								</Link>
							</div>

							{renderWorkspace ? (
								renderWorkspace(evalId)
							) : (
								<Card>
									<CardHeader className="pb-3">
										<div className="flex items-start gap-3">
											<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 text-amber-700 shrink-0">
												<Construction className="w-5 h-5" />
											</div>
											<div className="min-w-0">
												<CardTitle className="text-lg font-semibold flex items-center gap-2">
													{moduleName} workspace
													<Badge className="bg-amber-100 text-amber-700 text-[10px]">
														{workspacePhase}
													</Badge>
												</CardTitle>
												<p className="text-sm text-gray-500 mt-1">{workspaceHint}</p>
											</div>
										</div>
									</CardHeader>
									<CardContent>
										<p className="text-sm text-gray-600">
											The {moduleName.toLowerCase()} workspace lands in {workspacePhase} of
											the SSP Parity Rebuild. Until then, this surface confirms the route is
											wired and the evaluation can be picked. The real workspace will replace
											this card without changing the URL.
										</p>
										<p className="text-sm text-gray-600 mt-3">
											For now, manage {moduleName.toLowerCase()} from the evaluation detail
											page:{' '}
											<Link
												to={`/evaluations/${evalId}`}
												className="text-[#0080fe] hover:underline"
											>
												Open evaluation →
											</Link>
										</p>
									</CardContent>
								</Card>
							)}
						</div>
					)}
				</main>
			</div>
		</div>
	)
}

export default EvaluationModulePage
